import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  /** When this changes after a failure (a new model file), the boundary tries its children again. */
  resetKey: string;
  /** Drawn in the machine's place on failure. A three.js element, never DOM: this sits in the scene. */
  fallback: ReactNode;
  /** What the warning names, so the broken file can be found without the code. */
  label: { bID: string; name: string; modelFile: string };
  /** Called on every failure, after the warning (which is once per page load). */
  onFallback: () => void;
  children?: ReactNode;
}

interface State {
  error: unknown;
  failed: boolean;
}

/**
 * Machines already warned about, by bID and model file. The loader remembers a failure for the page
 * load, and the scene remounts its machines on many re-renders (a mode change, a twin refresh —
 * `TwinEntity3D`'s inline `ThisEntity`, a backlog item), so each remount fails again: this
 * boundary's own warning is once per machine and model file per page load, not once per remount.
 * (react-three-fiber's root still reports every caught error itself, through `reportError`.)
 */
const warned = new Set<string>();

/**
 * The loader's message, with the query string of any address in it removed: a model's signed
 * address carries the storage token there. The separator after the address is kept.
 */
const withoutQuery = (error: unknown) =>
  (error instanceof Error ? error.message : String(error)).replace(
    /\?[^\s"']*?(:?)(?=\s|$|["'])/g,
    '$1',
  );

/**
 * One machine's crash catcher. A model that fails to download or parse, or holds no mesh, draws
 * `fallback` (the placeholder) in its place instead of taking down the whole twin — the scene's
 * own `ErrorBoundary` sits round the whole map.
 */
export class MachineBoundary extends Component<Props, State> {
  state: State = { error: null, failed: false };

  static getDerivedStateFromError(error: unknown): State {
    return { error, failed: true };
  }

  componentDidCatch(error: unknown, _errorInfo: ErrorInfo) {
    const { bID, name, modelFile } = this.props.label;
    const key = `${bID}|${modelFile}`;
    if (!warned.has(key)) {
      warned.add(key);
      // Only this line is token-free: react-three-fiber's root also reports the raw error
      // (`reportError`), as it did before this boundary existed.
      console.warn(
        `[MachineBoundary] machine ${bID} "${name}" fell back to the placeholder: model ${modelFile} — ${withoutQuery(error)}`,
      );
    }
    this.props.onFallback();
  }

  componentDidUpdate(prev: Props, prevState: State) {
    // Only a failure from an EARLIER render resets: one caught in this same render (a new model
    // file that is already known to be broken) stays caught instead of failing twice.
    if (prevState.failed && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, failed: false });
    }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
